/**
 * Featured collection (TMB product cards) — arrow-only carousel.
 * Page scroll: moves by visible card count (3 / 2 / 1).
 * Set data-swipe-enabled="true" on [data-tmb-fcpc-root] to enable touch swipe later.
 */
(function () {
  'use strict';

  var SLOT_WIDTH = 338;
  var ARROW_WIDTH = 61;
  var CAROUSEL_MAX = 1202;
  var BP_DESKTOP = 1202;
  var BP_TWO = 874;
  var BP_ONE = 546;
  var MOBILE_MAX = 749;

  function getVisibleCount(viewportWidth) {
    if (viewportWidth <= MOBILE_MAX) return 1;
    if (viewportWidth >= BP_DESKTOP) return 3;
    if (viewportWidth >= BP_TWO) return 2;
    if (viewportWidth >= BP_ONE) return 1;
    return 1;
  }

  function isMobile(viewportWidth) {
    return viewportWidth <= MOBILE_MAX;
  }

  function carouselWidth(visibleCount) {
    return ARROW_WIDTH * 2 + visibleCount * SLOT_WIDTH;
  }

  function viewportWidth(visibleCount) {
    return visibleCount * SLOT_WIDTH;
  }

  function TmbFeaturedCollectionProductCards(root) {
    this.root = root;
    this.carousel = root.querySelector('[data-tmb-fcpc-carousel]');
    if (!this.carousel) return;

    this.track = root.querySelector('[data-tmb-fcpc-track]');
    this.prevBtn = root.querySelector('[data-tmb-fcpc-prev]');
    this.nextBtn = root.querySelector('[data-tmb-fcpc-next]');
    this.shell = this.carousel.querySelector('.tmb-fcpc__carousel-shell');
    this.viewport = root.querySelector('[data-tmb-fcpc-viewport]');
    this.slots = this.track ? this.track.querySelectorAll('[data-tmb-fcpc-slot]') : [];
    this.index = 0;
    this.swipeEnabled = root.getAttribute('data-swipe-enabled') === 'true';
    this.slotWidth = this.getCssPxVar('--tmb-fcpc-slot-w', SLOT_WIDTH);

    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.blockScroll = this.blockScroll.bind(this);

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
    window.addEventListener('resize', this.onResize);

    if (this.track) {
      this.track.addEventListener('wheel', this.onWheel, { passive: false });
      this.track.addEventListener('touchmove', this.blockScroll, { passive: false });
    }

    if (typeof ResizeObserver !== 'undefined' && this.viewport) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
    }

    this.update();
  }

  TmbFeaturedCollectionProductCards.prototype.getCssPxVar = function (name, fallback) {
    var value = window.getComputedStyle(this.root).getPropertyValue(name);
    var parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  TmbFeaturedCollectionProductCards.prototype.getTotal = function () {
    return this.slots.length;
  };

  TmbFeaturedCollectionProductCards.prototype.blockScroll = function (event) {
    if (!this.swipeEnabled) {
      event.preventDefault();
    }
  };

  TmbFeaturedCollectionProductCards.prototype.onWheel = function (event) {
    if (!this.swipeEnabled) {
      event.preventDefault();
    }
  };

  TmbFeaturedCollectionProductCards.prototype.getState = function () {
    var w = window.innerWidth;
    this.slotWidth = this.getCssPxVar('--tmb-fcpc-slot-w', SLOT_WIDTH);
    var total = this.getTotal();
    var requestedVisible = getVisibleCount(w);
    var sectionWidth = this.root.getBoundingClientRect().width || w;
    var availableCarouselWidth = Math.min(sectionWidth, w);
    var maxVisibleFit = Math.floor((availableCarouselWidth - ARROW_WIDTH * 2) / this.slotWidth);
    var visible = Math.max(1, Math.min(requestedVisible, maxVisibleFit > 0 ? maxVisibleFit : 1, total || 1));
    var maxIndex = Math.max(0, total - visible);
    var viewW = visible * this.slotWidth;
    var carouselW = ARROW_WIDTH * 2 + viewW;

    return {
      viewportWidth: w,
      sectionWidth: sectionWidth,
      availableCarouselWidth: availableCarouselWidth,
      visible: visible,
      total: total,
      maxIndex: maxIndex,
      mobile: isMobile(w),
      carouselW: carouselW,
      viewW: viewW,
    };
  };

  TmbFeaturedCollectionProductCards.prototype.clampIndex = function (index, maxIndex) {
    if (index < 0) return 0;
    if (index > maxIndex) return maxIndex;
    return index;
  };

  TmbFeaturedCollectionProductCards.prototype.applyLayout = function (state) {
    var cappedCarousel = Math.min(CAROUSEL_MAX, state.carouselW);
    if (state.viewportWidth < BP_DESKTOP && !state.mobile) {
      cappedCarousel = Math.min(state.availableCarouselWidth, state.carouselW);
    }

    this.root.style.setProperty('--tmb-fcpc-visible', String(state.visible));
    this.root.style.setProperty('--tmb-fcpc-carousel-w', cappedCarousel + 'px');
    this.root.style.setProperty('--tmb-fcpc-viewport-w', state.viewW + 'px');

    if (this.shell && !state.mobile) {
      this.shell.style.width = cappedCarousel + 'px';
    } else if (this.shell) {
      this.shell.style.width = '';
    }
  };

  TmbFeaturedCollectionProductCards.prototype.getSlotStep = function () {
    if (!this.slots.length) return this.slotWidth;
    var width = this.slots[0].getBoundingClientRect().width;
    return width > 0 ? width : this.slotWidth;
  };

  TmbFeaturedCollectionProductCards.prototype.applyTransform = function () {
    var offset = this.index * this.getSlotStep();
    if (this.track) {
      this.track.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';
    }
  };

  TmbFeaturedCollectionProductCards.prototype.updateButtons = function (state) {
    var atStart = this.index <= 0;
    var atEnd = this.index >= state.maxIndex;

    if (this.prevBtn) {
      if (atStart) {
        this.prevBtn.setAttribute('hidden', '');
        this.prevBtn.disabled = true;
      } else {
        this.prevBtn.removeAttribute('hidden');
        this.prevBtn.disabled = false;
      }
    }

    if (this.nextBtn) {
      if (atEnd || state.total <= state.visible) {
        this.nextBtn.setAttribute('hidden', '');
        this.nextBtn.disabled = true;
      } else {
        this.nextBtn.removeAttribute('hidden');
        this.nextBtn.disabled = false;
      }
    }
  };

  TmbFeaturedCollectionProductCards.prototype.update = function () {
    var state = this.getState();
    this.index = this.clampIndex(this.index, state.maxIndex);
    this.applyLayout(state);
    this.applyTransform();
    this.updateButtons(state);
  };

  TmbFeaturedCollectionProductCards.prototype.onResize = function () {
    this.update();
  };

  TmbFeaturedCollectionProductCards.prototype.onPrev = function () {
    var state = this.getState();
    this.index = this.clampIndex(this.index - state.visible, state.maxIndex);
    this.update();
  };

  TmbFeaturedCollectionProductCards.prototype.onNext = function () {
    var state = this.getState();
    this.index = this.clampIndex(this.index + state.visible, state.maxIndex);
    this.update();
  };

  function initRoot(root) {
    if (root._tmbFcpcInit) return;
    root._tmbFcpcInit = true;
    if (root.getAttribute('data-display-type') !== 'slideshow') return;
    new TmbFeaturedCollectionProductCards(root);
  }

  function initAll() {
    document.querySelectorAll('[data-tmb-fcpc-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-tmb-fcpc-root]');
    if (root) {
      root._tmbFcpcInit = false;
      initRoot(root);
    }
  });
})();
